import { FC, useCallback, useDeferredValue } from 'react';
import getClassNames from 'src/common/utils/getClassNames';
import classNames from 'classnames';
import WarningDS22 from 'src/common/components/WarningDS22';
import { useTranslation } from 'next-i18next';
import { useTripCartOptionsContext } from 'src/trip-cart-deprecated/hooks/useTripCartOptionsContext';
import Button from 'src/common/components/ButtonDS22';
import { useTripCartContext } from 'src/trip-cart-deprecated/hooks/useTripCartContext';
import { useTripCartBookingContext } from 'src/trip-cart-deprecated/hooks/useTripCartBookingContext';
import { useForm, useFormState } from 'react-final-form';
import { TripCartFormState } from 'src/listing/types';
import { trackEvent } from 'src/common/3rdParty/tracking';
import { getListingLocationSlug } from 'src/common/helpers';
import { trackTripCartBookNowClick, trackTripCartInquiryClick } from 'src/trip-cart-deprecated/utils/trackingTripCart';
import { BOOKING_MODE } from 'src/listing/constants';
import { formatDate } from 'src/common/utils/dateHelpers';
import styles from './TripCartBookingButtons.module.scss';

type TripCartBookingButtonsProps = {
  position: 'trip-cart-cost-panel' | 'trip-cart-bottom-sheet--open' | 'trip-cart-bottom-sheet--closed';
  overrideClick?: () => void;
};
const TripCartBookingButtons: FC<TripCartBookingButtonsProps> = ({ position, overrideClick }) => {
  const [t] = useTranslation('listing-page');
  const { isSubmitting } = useTripCartBookingContext();
  const { options } = useTripCartOptionsContext();
  const { listing, showAiSummary } = useTripCartContext();
  const { submit, change } = useForm<TripCartFormState>();
  const {
    values: {
      bookingMode,
      formSelectedDates,
      formDuration,
      formGuests,
      withCaptain,
      isMultiDay,
      departureTime,
    },
    errors,
  } = useFormState<TripCartFormState>();
  const { instabookVisible, inquiryEnabled, instabookEnabled } = useDeferredValue(
    options.actions,
  );
  const { isOwnBoat } = options.actions;

  const submitInquiry = useCallback((buttonType: 'send-inquiry-button' | 'contact-owner-button') => {
    if (!inquiryEnabled) {
      return;
    }

    const listingLocation = getListingLocationSlug(listing.breadcrumbs);

    // Only track the event if we have a departure time
    if (departureTime && !errors?.departureTime) {
      trackEvent('inquiry_start', {
        p1: buttonType,
        p2: position,
        p3: showAiSummary ? 'ai_summary_visible' : 'ai_summary_hidden',
        ...(listingLocation ? { listing_location: listingLocation } : {}),
      });
    }

    trackTripCartInquiryClick({
      dates: formSelectedDates.map((date) => formatDate(date)),
      duration: formDuration,
      capacity: formGuests.toString(),
      captainOption: withCaptain.toString(),
      bookNowVisible: instabookVisible ? 'true' : 'false',
      bookNowEnabled: instabookEnabled ? 'true' : 'false',
      sameOrMultiDay: isMultiDay ? 'multi-day' : 'hourly',
    });

    change(BOOKING_MODE, 'inquiry');
    submit();
  }, [
    errors,
    change,
    formDuration,
    formGuests,
    formSelectedDates,
    inquiryEnabled,
    instabookEnabled,
    instabookVisible,
    isMultiDay,
    listing.breadcrumbs,
    position,
    submit,
    withCaptain,
    departureTime,
    showAiSummary,
  ]);

  const submitInstabook = useCallback(() => {
    if (!instabookEnabled) {
      return;
    }
    if (departureTime && !errors?.departureTime) {
      trackTripCartBookNowClick('success');
    } else {
      trackTripCartBookNowClick('validation-error');
    }

    change(BOOKING_MODE, 'instabook');
    submit();
  }, [change, instabookEnabled, submit, departureTime, errors]);

  return (
    <div className={getClassNames(
      styles,
      'root',
      classNames({ withWarning: isOwnBoat }),
    )}
    >
      {isOwnBoat && (
        <div className={styles.warning}>
          <WarningDS22
            iconSize="m"
            showIcon
            centered
          >
            {t('pricing-structure--owner-inquiry-warning')}
          </WarningDS22>
        </div>
      )}

      {!instabookVisible && (
        <Button
          type="button"
          variant="primary-blue"
          data-test="booking"
          disabled={!inquiryEnabled || isSubmitting || isOwnBoat}
          loading={isSubmitting && bookingMode === 'inquiry'}
          onClick={overrideClick || (() => submitInquiry('send-inquiry-button'))}
          onAuxClick={overrideClick || (() => submitInquiry('send-inquiry-button'))}
        >
          {t('price-panel--button--booking-inquiry')}
        </Button>
      )}

      {instabookVisible && (
        <>
          <div className={getClassNames(
            styles,
            'inquiryInfo',
            classNames({ withWarning: isOwnBoat }),
          )}
          >
            <Button
              type="button"
              data-test="booking"
              onClick={overrideClick || (() => submitInquiry('contact-owner-button'))}
              onAuxClick={overrideClick || (() => submitInquiry('contact-owner-button'))}
              loading={isSubmitting && bookingMode === 'inquiry'}
              variant="tertiary-deemphasized"
              fitWidth
              disabled={!inquiryEnabled || isSubmitting || isOwnBoat}
              icon="chat"
              iconPosition="right"
              size="x-small"
            >
              {t('price-panel--button--booking-inquiry')}
            </Button>
          </div>

          <div className={getClassNames(
            styles,
            'bookNowButton',
            classNames({ withWarning: isOwnBoat }),
          )}
          >
            <Button
              type="button"
              variant="instabook-green"
              data-test="instabook"
              loading={isSubmitting && bookingMode === 'instabook'}
              disabled={!instabookEnabled || isSubmitting || isOwnBoat}
              onClick={overrideClick || submitInstabook}
              onAuxClick={overrideClick || submitInstabook}
            >
              {t('price-panel--button--instabook')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default TripCartBookingButtons;
